import React, { useState } from 'react';
import { StatusBadge } from './ui';
import { Callout } from './primitives';
import { actionLabel } from './operation/actionLabels';
import { FactList } from './project/ui';
import { labelFor, remediationActionTypeLabel, remediationTargetTypeLabel, resourceOwnershipLabel } from '../labels';
import {
  RemediationAction,
  approveRemediationAction,
  executeRemediationAction,
  cancelRemediationAction,
} from '../api';

interface RemediationActionCardProps {
  action: RemediationAction;
  onActionUpdated?: (updated: RemediationAction) => void;
}

export const RemediationActionCard: React.FC<RemediationActionCardProps> = ({
  action,
  onActionUpdated,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ title: string; message: string; retry: () => void } | null>(null);

  const handleApprove = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await approveRemediationAction(action.id);
      onActionUpdated?.(res.action);
    } catch (err: any) {
      setError({ title: 'The fix was not approved', message: err?.message || 'No reason was given.', retry: handleApprove });
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await executeRemediationAction(action.id);
      onActionUpdated?.(res.action);
    } catch (err: any) {
      setError({ title: 'The fix was not run', message: err?.message || 'No reason was given.', retry: handleExecute });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await cancelRemediationAction(action.id, 'Cancelled from dashboard');
      onActionUpdated?.(res.action);
    } catch (err: any) {
      setError({ title: 'The fix was not dismissed', message: err?.message || 'No reason was given.', retry: handleCancel });
    } finally {
      setLoading(false);
    }
  };

  const awaitingApproval = action.state === 'waiting_for_approval' || action.state === 'proposed';
  const verified = action.verification_status && action.verification_status !== 'pending';

  const facts: { label: string; value: React.ReactNode }[] = [
    {
      label: 'Target',
      value: (
        <>
          {labelFor(remediationTargetTypeLabel, action.target_resource_type)} <code>{action.target_resource_id}</code>
        </>
      ),
    },
    { label: 'Run by', value: labelFor(resourceOwnershipLabel, action.target_resource_ownership) },
  ];
  if (action.eligibility_reason) facts.push({ label: 'Policy', value: action.eligibility_reason });
  // The engine's id for the action: evidence for a developer, not a heading.
  facts.push({ label: 'Technical name', value: <code translate="no">{action.action_type}</code> });
  if (verified) {
    facts.push({
      label: 'Verification',
      value: (
        <span className="remediation-card__verification">
          <StatusBadge kind="checkResult" value={action.verification_status} />
          {action.completed_at ? (
            <span className="type-meta">Completed at {new Date(action.completed_at).toLocaleTimeString()}</span>
          ) : null}
        </span>
      ),
    });
  }

  return (
    <article data-testid={`remediation-card-${action.id}`} className="remediation-card">
      <div className="remediation-card__head">
        <div className="remediation-card__title">
          <span className="remediation-card__name">{labelFor(remediationActionTypeLabel, action.action_type)}</span>
          <StatusBadge kind="remediationAction" value={action.state} />
          <StatusBadge kind="remediationRisk" value={action.risk} />
        </div>

        {awaitingApproval || action.state === 'approved' ? (
          <div className="remediation-card__actions">
            {awaitingApproval ? (
              <>
                <button type="button" onClick={handleCancel} disabled={loading} className="button button--ghost button--small">
                  Dismiss
                </button>
                <button type="button" onClick={handleApprove} disabled={loading} className="button button--secondary button--small">
                  Approve and run
                </button>
              </>
            ) : (
              <button type="button" onClick={handleExecute} disabled={loading} className="button button--secondary button--small">
                Run now
              </button>
            )}
          </div>
        ) : null}
      </div>

      <FactList facts={facts} />

      {action.state === 'escalated' ? (
        <Callout tone="danger" title="Fixes on this target are paused">
          <p>
            Failed too many times in a row, so Commerce Ops stopped running them. Find the cause before allowing them again.
          </p>
          {action.failure_reason ? <p>Last failure: {action.failure_reason}</p> : null}
        </Callout>
      ) : null}

      {action.failure_reason && action.state !== 'escalated' ? (
        <Callout tone="danger" title="The fix failed">
          {action.failure_reason}
        </Callout>
      ) : null}

      {error ? (
        <Callout
          tone="danger"
          title={error.title}
          actions={
            <button type="button" className="button button--secondary button--small" onClick={error.retry} disabled={loading}>
              {actionLabel.retry}
            </button>
          }
        >
          {error.message}
        </Callout>
      ) : null}
    </article>
  );
};
